import { mhvillageApi } from "api/mhvillage";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Card } from "shared/components/Card";
import { Unlink02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

export const MHVillageView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData } = useAuthContext();

  const {
    data: status,
    isLoading,
    isError
  } = useQuery({
    queryKey: ["MHVILLAGE", "STATUS"],
    queryFn: mhvillageApi.getStatus
  });

  const { mutate: disconnect, isPending: isDisconnecting } = useMutation({
    mutationKey: ["MHVILLAGE", "DELETE"],
    mutationFn: () => mhvillageApi.disconnect(),
    onSuccess: () => {
      toast.success("MHVillage integration disconnected successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <Layout
      header={
        <>
          <div className="flex items-center justify-between border-b border-alpha-40 px-3 md:px-6 pb-3">
            <BreadCrumbs
              crumbs={[
                { title: "Settings", href: "/settings" },
                { title: "Integration", href: "/settings/integration" },
                {
                  title: "MHVillage",
                  href: "/settings/integration/mhvillage"
                }
              ]}
            />
          </div>
          <div className="h-[88px] flex items-center justify-between px-6 py-4 border-b border-alpha-40">
            <div className="flex items-center gap-4">
              <IntegrationLogoIcon id={IntegrationId.MHVILLAGE} size="SM" />
              <p className="text-xl font-bold text-content-1000">MHVillage</p>
            </div>
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Unlink02Icon}
              onClick={() => disconnect()}
              loading={isDisconnecting}
            >
              Disconnect
            </Button>
          </div>
          <div className="px-6 h-[76px] flex flex-col justify-center gap-2 text-sm">
            <p className="text-content-400">Integration type</p>
            <p className="font-medium text-content-1000">MHVillage</p>
          </div>
        </>
      }
    >
      <Card className="w-full p-6">
        <p className="text-sm text-content-400">Parsing email</p>
        <Show if={isError}>
          <p className="mt-1 text-base text-content-1000">
            Unable to load the parsing email. Please try again later.
          </p>
        </Show>
        <Show if={!isError}>
          <p className="mt-2 text-base font-bold text-content-1000">
            {isLoading ? "Loading…" : (status?.parsing_email ?? "—")}
          </p>
        </Show>
      </Card>
    </Layout>
  );
};
